import React, { useState } from "react";
import { type BuildAndBuyDispatchType, useBnbSelector } from "../../../../redux/store";
import Price from "../../common/price/Price";
import PriceFootNote from "../../common/price/PriceFootNote";
import { BnbLabel, useBnbOptionalLabel } from "../../../../utils/labels";
import ExteriorSpin from "./ExteriorSpin";
import InteriorGallery from "./InteriorGallery";
import Personalization from "./Personalization";
import Container from "../../../../../../../common-deprecated/styles/v2/lexus/globals/Container";
import * as Styles from "./styles/CarHero";
import WinterWheelToggle from "../../common/WinterWheelToggle";
import { IconWrapper } from "../../../../../../../common-deprecated/styles/v2/lexus/globals/IconWrapper";
import InteriorSpin3D from "./InteriorSpin3D";
import useWebGLSupportLevel from "../../../../hooks/customize/useWebGLSupportLevel";
import CustomizeCtaContainer from "../../common/cta/CustomizeCtaContainer";
import { setFinanceOption } from "../../../../../../../common-deprecated/redux/actions/UtilActions";
import TopSpinViewToggle from "./TopSpinViewToggle";
import { NavStep } from "../../../../../shared-logic/utils/Navigation";
import Icon from "../../../../../../../common-deprecated/styles/v2/lexus/globals/Icon";
import { hasWinterWheelInBasket } from "../../../../utils/typedUtils";
import { MAIN_NAV_CLASSNAME_TOYOTA } from "../../../../../../../common-deprecated/constants";
import UnsetZIndexHelper from "../../../../../../../common-deprecated/components/UnsetZIndexHelper";
import { shouldHideCarHeaderPrice, showNextGenInteriorDisclaimer } from "../../../../utils/settings/bnbSettingUtils";
import { trackCustomizeFullscreen, trackFinanceToggle } from "../../../../utils/tracking";
import { getEcoBonusPromotions } from "../../../../../../../common-deprecated/utils/promotionUtils";
import EcoBonusPromotion from "../../../../../../../common-deprecated/components/aem/lexus/EcoBonusPromotion";
import type { PricePromotionType } from "../../../../../../../common-deprecated/types/CommonTypes";
import AvailabilityBadges from "../../../../../../../common-deprecated/components/aem/lexus/availability-badges/AvailabilityBadges";
import { getInteriorSpinFlags } from "../../../../../../shared-logic/utils/imageUtils";
import { useDispatch } from "../../../../../../../shared-logic/redux/store";

const CarHero = (): JSX.Element => {
    const [spinFullscreen, setSpinFullscreen] = useState<boolean>(false);

    const dispatch = useDispatch<BuildAndBuyDispatchType>();
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const car = useBnbSelector((state) => state.car);
    const activePersonalization = useBnbSelector((state) => state.utils.activePersonalization);
    const hideCarHeaderPrice = useBnbSelector(({ bnbSettings, utils }) => shouldHideCarHeaderPrice(bnbSettings, utils));
    const hasWinterWheel = useBnbSelector((state) => hasWinterWheelInBasket(state.basket));
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);
    const financeOption = useBnbSelector((state) => state.utils.financeOption);
    const visibleInTopSpin = useBnbSelector((state) => state.car.visibleInTopSpin);
    const totalPrice = useBnbSelector((state) => state.basket.total);
    const ecoBonus = useBnbSelector((state) =>
        getEcoBonusPromotions<PricePromotionType>(state.items.promotions, state.car.price.promotions),
    );

    const webGLSupportLevel = useWebGLSupportLevel();
    const interiorSpinFlags = getInteriorSpinFlags(commonSettings, car, activePersonalization, webGLSupportLevel);
    const showNextGenInteriorDisclaimerSetting = showNextGenInteriorDisclaimer(
        bnbSettings,
        commonSettings,
        car.model.id,
    );

    //  currently not used
    //  const iconResizeSmallColor = activePersonalization === "upholstery" ? "lightest" : "dark";
    const [financeOptionSwitchLabel, genericePriceDisclaimerCustomize] = useBnbOptionalLabel([
        financeOption === "cash" ? "financeOptionShortSwitchMonthly" : "financeOptionShortSwitchCash",
        "genericPriceDisclaimerCustomize",
    ]);

    const toggleFinanceOption = (): void => {
        const newOption = financeOption === "cash" ? "monthly" : "cash";
        trackFinanceToggle(NavStep.Customize, newOption, "cc-action");
        dispatch(setFinanceOption(newOption));
    };

    return (
        <Styles.Wrapper>
            <UnsetZIndexHelper enabled={spinFullscreen} selectors={[MAIN_NAV_CLASSNAME_TOYOTA]} />
            <Styles.ResizeWrapper fullscreen={spinFullscreen}>
                <Styles.HeaderWrapper isFullscreen={spinFullscreen} hasLightColors={interiorSpinFlags.enabled}>
                    <Container>
                        <Styles.Header>
                            <Styles.LeftCorner
                                width={{ _: 1 / 2, lg: 3 / 12 }}
                                mb={{ _: 3, md: "auto" }}
                                hasWinterWheel={hasWinterWheel}
                            >
                                <Styles.ModelTitle>{car.model.webtitleSectionName}</Styles.ModelTitle>
                                {car.grade.webtitleSectionName && (
                                    <Styles.GradeTitle>{car.grade.webtitleSectionName}</Styles.GradeTitle>
                                )}
                                {car.body.webtitleSectionName && (
                                    <Styles.GradeTitle>{car.body.webtitleSectionName}</Styles.GradeTitle>
                                )}

                                {!hideCarHeaderPrice && (
                                    <Styles.PriceWrapper>
                                        <EcoBonusPromotion ecoBonus={ecoBonus} />
                                        <Price
                                            price={totalPrice}
                                            isLarge
                                            financeItemId="total"
                                            forceMonthlyDisclaimer
                                            hideWhenLoading
                                            cashPriceDisclaimer={genericePriceDisclaimerCustomize}
                                        />
                                        <PriceFootNote />
                                    </Styles.PriceWrapper>
                                )}

                                <AvailabilityBadges
                                    isSoldOut={car.soldOut}
                                    isAvailableForOnlineSales={car.availableForOnlineSales}
                                    isAvailableForOnlineLeasing={car.availableForOnlineLeasing}
                                />

                                {financeOptionSwitchLabel && (
                                    <Styles.FinanceToggle onClick={toggleFinanceOption}>
                                        {financeOptionSwitchLabel}
                                    </Styles.FinanceToggle>
                                )}
                            </Styles.LeftCorner>
                            <Styles.Middle
                                interiorSpinFlags={interiorSpinFlags.enabled}
                                isFullscreen={spinFullscreen}
                                width={{ _: 1, lg: 7 / 12 }}
                            />
                            <Styles.RightCorner
                                width={{ _: 1 / 2, lg: 2 / 12 }}
                                isFullscreen={spinFullscreen}
                                hasLightColor={interiorSpinFlags.enabled}
                                hasWinterWheel={hasWinterWheel}
                            >
                                <Styles.Toggle
                                    hasWinterWheel={hasWinterWheel}
                                    isFullscreen={spinFullscreen}
                                    onClick={() => {
                                        setSpinFullscreen((state) => !state);
                                        if (!spinFullscreen) trackCustomizeFullscreen(activePersonalization);
                                    }}
                                >
                                    <span className="sr-only">
                                        <BnbLabel label={spinFullscreen ? "spinFullscreenClose" : "spinFullscreen"} />
                                    </span>
                                    <IconWrapper iconColor="lightest">
                                        <Icon variant={spinFullscreen ? "close" : "fullscreen"} />
                                    </IconWrapper>
                                </Styles.Toggle>

                                <CustomizeCtaContainer />

                                {hasWinterWheel && <WinterWheelToggle />}
                            </Styles.RightCorner>
                            <Styles.ExteriorSpinWrapper
                                width={{ _: 1, lg: 9 / 12 }}
                                isFullscreen={spinFullscreen}
                                hidden={interiorSpinFlags.enabled}
                                hasHeaderPrice={!hideCarHeaderPrice}
                            >
                                <ExteriorSpin isFullscreen={spinFullscreen} />
                            </Styles.ExteriorSpinWrapper>
                        </Styles.Header>
                    </Container>
                </Styles.HeaderWrapper>

                <Styles.InteriorGalleryWrapper isFullscreen={spinFullscreen}>
                    {interiorSpinFlags.interior3D && <InteriorSpin3D visible fullscreen={spinFullscreen} />}
                    {interiorSpinFlags.interior2D && <InteriorGallery isFullscreen={spinFullscreen} />}
                    {/* Required as per design, just show a * which references the actual disclaimer underneath the personalisation value see Personalization.tsx */}
                    {activePersonalization === "upholstery" && showNextGenInteriorDisclaimerSetting && (
                        <Styles.DisclaimerReference>*</Styles.DisclaimerReference>
                    )}
                </Styles.InteriorGalleryWrapper>
            </Styles.ResizeWrapper>

            <Container position="relative">
                {visibleInTopSpin && <TopSpinViewToggle />}
                <Styles.Toolbar width={1}>
                    <Personalization />

                    <CustomizeCtaContainer />
                </Styles.Toolbar>
            </Container>
        </Styles.Wrapper>
    );
};

export default CarHero;
